import React, { useMemo } from 'react';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../i18n';
import { PILLAR_COLORS, PILLAR_ORDER, toPoints } from '../../utils/energyPalette';
import { pillarLabel } from './energyLabels';

// ─────────────────────────────────────────────────────────────────────────
// DailyEnergyDayView (#385) — what one bar of the daily chart is made of.
//
// An INLINE view inside the panel's drill card, not a modal: it is one more
// step of the same drill-down the pizza already offers (overview → day →
// pillar → category → items), so it lives where that drill-down lives and
// needs no portal, no focus trap and no Escape handling to keep from tearing
// down the panel behind it (the #272/#302 lessons simply do not arise).
//
// Every number comes from the SAME /api/energy/daily payload the bar was drawn
// from — no second request — and the points are printed verbatim (2dp strings).
// A pillar row drills into the existing Level 2/3 components scoped to this
// single day, which the backend serves from the same rows, so the figures the
// user drills into always add back up to the one they clicked.
// ─────────────────────────────────────────────────────────────────────────

// One plain-language line per pillar: what the energy in it was spent DOING.
// Kept in step with the BE taxonomy (app/utils/energy_pillars.py).
const DESCRIPTIONS = {
    listening: ['energyDayDesc_listening', 'Turning what you said or uploaded into something PAI can use: voice transcription and indexing.'],
    viewing: ['energyDayDesc_viewing', 'Reading images and scans: OCR, receipts and business cards.'],
    writing: ['energyDayDesc_writing', 'Producing text for you: documents, edits, summaries and reports.'],
    reasoning: ['energyDayDesc_reasoning', 'Thinking: understanding requests, researching, searching the web and composing answers.'],
    executing: ['energyDayDesc_executing', 'Acting on your data: creating, updating, moving and deleting records.'],
};

const ShareBar = ({ rows, total }) => {
    // The day's split across pillars as one proportional strip. SVG `<rect>`s so
    // each segment's colour is a literal `fill` attribute — the same locked
    // palette, assertable exactly as the pizza's slices are.
    let x = 0;
    return (
        <svg
            data-testid="energy-day-share"
            viewBox="0 0 100 6"
            preserveAspectRatio="none"
            width="100%"
            height="8"
            aria-hidden="true"
            className="block rounded-[3px] overflow-hidden"
        >
            <rect x="0" y="0" width="100" height="6" fill="rgb(255 255 255 / 0.08)" />
            {total > 0 && rows.map((r) => {
                if (r.value <= 0) return null;
                const w = (r.value / total) * 100;
                const rect = (
                    <rect
                        key={r.pillar}
                        data-testid={`energy-day-share-${r.pillar}`}
                        x={x}
                        y="0"
                        width={w}
                        height="6"
                        fill={PILLAR_COLORS[r.pillar]}
                    />
                );
                x += w;
                return rect;
            })}
        </svg>
    );
};

const DailyEnergyDayView = ({ day, onBack, onSelectPillar }) => {
    const { t, language } = useLanguage();

    const rows = useMemo(() => {
        const byName = new Map((day?.pillars || []).map((p) => [p?.pillar, p]));
        return PILLAR_ORDER.map((pillar) => {
            const row = byName.get(pillar);
            return { pillar, pts: row?.pts ?? '0.00', value: Math.max(0, toPoints(row?.pts)) };
        });
    }, [day]);
    const total = rows.reduce((acc, r) => acc + r.value, 0);

    const dateLabel = useMemo(() => {
        if (!day?.date) return '';
        try {
            return new Intl.DateTimeFormat(language === 'es' ? 'es' : 'en', {
                weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
            }).format(new Date(`${day.date}T00:00:00Z`));
        } catch {
            return day.date;
        }
    }, [day, language]);

    const pts = t('energyPtsSuffix', 'pts');

    return (
        <section
            data-testid="energy-day-view"
            data-date={day?.date || ''}
            aria-label={dateLabel}
            className="min-w-0 max-w-full"
        >
            <header className="flex items-start gap-2 min-w-0">
                <button
                    type="button"
                    data-testid="energy-day-back"
                    onClick={onBack}
                    aria-label={t('energyDayBack', 'Back to the range')}
                    title={t('energyDayBack', 'Back to the range')}
                    className="pai-icon-btn w-7 h-7 flex-shrink-0"
                >
                    <ArrowLeft size={14} />
                </button>
                <div className="min-w-0 flex-1">
                    <div className="pai-eyebrow truncate">{t('energyDayEyebrow', 'Energy used on')}</div>
                    <div className="text-[15px] font-semibold text-white break-words">{dateLabel}</div>
                </div>
                <div className="flex-shrink-0 text-right">
                    <div className="pai-eyebrow">{t('energyTotalPoints', 'Total points')}</div>
                    <div className="text-[15px] font-bold text-white tnum">
                        {/* Verbatim 2dp string. */}
                        <span data-testid="energy-day-total">{day?.total_pts ?? '0.00'}</span>
                        <span className="ml-1 text-[12px] font-medium text-pai-meta">{pts}</span>
                    </div>
                </div>
            </header>

            {total <= 0 ? (
                <p data-testid="energy-day-none" className="mt-3 text-[13px] text-pai-meta break-words">
                    {t('energyDayNone', 'No energy was used on this day.')}
                </p>
            ) : (
                <div className="mt-3">
                    <ShareBar rows={rows} total={total} />
                </div>
            )}

            <ul className="mt-3 space-y-1.5 min-w-0">
                {rows.map((r) => {
                    const zero = r.value <= 0;
                    const [descKey, descFallback] = DESCRIPTIONS[r.pillar];
                    const pct = total > 0 ? `${((r.value / total) * 100).toFixed(1)}%` : '0.0%';
                    return (
                        <li key={r.pillar} className="min-w-0">
                            <button
                                type="button"
                                data-testid={`energy-day-pillar-${r.pillar}`}
                                data-zero={zero ? 'true' : 'false'}
                                disabled={zero}
                                onClick={() => !zero && onSelectPillar?.(r.pillar)}
                                className={`group w-full text-left px-3 py-2.5 rounded-row border border-pai-subtle bg-white/[0.025] min-w-0 max-w-full transition-colors ${
                                    zero ? 'cursor-default' : 'hover:bg-white/[0.07] hover:border-pai'
                                }`}
                            >
                                <span className={`flex items-center gap-2.5 min-w-0 ${zero ? 'opacity-50' : ''}`}>
                                    <svg
                                        aria-hidden="true"
                                        width="11"
                                        height="11"
                                        viewBox="0 0 11 11"
                                        className="flex-shrink-0"
                                    >
                                        <rect
                                            data-testid={`energy-day-swatch-${r.pillar}`}
                                            width="11"
                                            height="11"
                                            rx="3"
                                            fill={PILLAR_COLORS[r.pillar]}
                                        />
                                    </svg>
                                    <span className="text-[14px] font-medium text-pai-body truncate min-w-0 flex-1">
                                        {pillarLabel(t, r.pillar)}
                                    </span>
                                    <span
                                        data-testid={`energy-day-pillar-pts-${r.pillar}`}
                                        className="text-[13.5px] font-bold text-white tnum flex-shrink-0"
                                    >
                                        {r.pts}
                                    </span>
                                    <span
                                        data-testid={`energy-day-pillar-pct-${r.pillar}`}
                                        className="text-[12.5px] text-pai-meta tnum flex-shrink-0 w-12 text-right"
                                    >
                                        {pct}
                                    </span>
                                    {!zero && <ChevronRight size={14} className="text-pai-meta flex-shrink-0" />}
                                </span>
                                <span
                                    data-testid={`energy-day-pillar-desc-${r.pillar}`}
                                    className={`mt-1 block text-[12px] text-pai-meta break-words ${zero ? 'opacity-60' : ''}`}
                                >
                                    {t(descKey, descFallback)}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
};

export default DailyEnergyDayView;
